var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Typography } from '@mui/material';
import { useCallback, useMemo, useState, } from 'react';
import { useTranslate } from 'react-admin';
import { SearchInput } from './SearchInput';
import { SearchResultContextProvider } from './SearchResultContext';
import { SearchResultsPanel } from './SearchResultsPanel';
import { useSearchController, } from './useSearchController';
/**
 * A component which displays an input, calls the dataProvider `search` method
 * when the input changes and shows the search results inside a Material UI
 * [`<List>`](https://mui.com/components/list/), just below the input.
 *
 * By default, it will group the search result items by resource and show their
 * `content.label` and `content.description`.
 *
 * @param props {SearchWithResultProps}
 * @param props.children {ReactElement} A React Element which will be rendered inside the Material UI `<List>`
 * @param props.queryOptions {QueryOptions} `@tanstack/react-query` options
 *
 * @example <caption>Here's how to include it inside the `<Admin>` `dashboard`</caption>
 * import { Card, CardContent } from '@mui/material';
 * import { Admin } from 'react-admin';
 * import { SearchWithResult } from '@react-admin/ra-search';
 * import { searchDataProvider } from './searchDataProvider';
 *
 * const MyDashboard = () => (
 *     <Card>
 *         <CardContent>
 *             <SearchWithResult />
 *         </CardContent>
 *     </Card>
 * );
 *
 * export const App = () => (
 *     <Admin dataProvider={searchDataProvider} dashboard={MyDashboard}>
 *         {...}
 *     </Admin>
 * );
 *
 * @example <caption>Customizing the <SearchResultsPanel> Content</caption>
 * import { Card, CardContent } from '@mui/material';
 * import { Link } from 'react-router-dom';
 * import { Admin } from 'react-admin';
 * import { SearchWithResult, SearchResultsPanel, useSearchResults} from '@react-admin/ra-search';
 * import { searchDataProvider } from './searchDataProvider';
 *
 * const MyDashboard = () => (
 *     <Card>
 *         <CardContent>
 *             <MySearchResultsPanel />
 *         </CardContent>
 *     </Card>
 * );
 *
 * const MySearchResultsPanel = () => {
 *     const { data } = useSearchResults();
 *     return (
 *         <ul style={{ maxHeight: '250px', overflow: 'auto' }}>
 *             {data.map(item => (
 *                 <li key={item.id}>
 *                     <Link to={item.url}>
 *                         <strong>{item.content.label}</strong>
 *                     </Link>
 *                     <p>{item.content.description}</p>
 *                 </li>
 *             ))}
 *         </ul>
 *     );
 * };
 *
 * export const App = () => (
 *     <Admin dataProvider={searchDataProvider} dashboard={MyDashboard}>
 *         {...}
 *     </Admin>
 * );
 *
 * @example <caption>Customizing the <SearchResultItem> Component</caption>
 * import { Card, CardContent } from '@mui/material';
 * import Groups3Icon from '@mui/icons-material/Groups3';
 * import LibraryMusicIcon from '@mui/icons-material/LibraryMusic';
 * import { Admin } from 'react-admin';
 * import { SearchWithResult, SearchResultsPanel, SearchResultItem, useSearchResults} from '@react-admin/ra-search';
 * import { searchDataProvider } from './searchDataProvider';
 *
 * const MyDashboard = () => (
 *     <Card>
 *         <CardContent>
 *             <SearchWithResult>
 *                 <SearchResultsPanel>
 *                     <SearchResultItem
 *                         label={record => (
 *                             <>
 *                                 {record.type === 'artists' ? (
 *                                     <Groups3Icon />
 *                                 ) : (
 *                                     <LibraryMusicIcon />
 *                                 )}
 *                                 <span>{record.content.label}</span>
 *                             </>
 *                         )}
 *                     />
 *                 </SearchResultsPanel>
 *             </SearchWithResult>
 *         </CardContent>
 *     </Card>
 * );
 *
 * export const App = () => (
 *     <Admin dataProvider={searchDataProvider} dashboard={MyDashboard}>
 *         {...}
 *     </Admin>
 * );
 */
export var SearchWithResult = function (props) {
    var _a = props.children, children = _a === void 0 ? _jsx(SearchResultsPanel, { disablePadding: true }) : _a, options = props.options, queryOptions = props.queryOptions, _b = props.wait, wait = _b === void 0 ? 500 : _b, _c = props.onNavigate, onNavigate = _c === void 0 ? function () { return undefined; } : _c, _d = props.disableHighlight, disableHighlight = _d === void 0 ? false : _d, rest = __rest(props, ["children", "options", "queryOptions", "wait", "onNavigate", "disableHighlight"]);
    var translate = useTranslate();
    var _e = useState(false), open = _e[0], setOpen = _e[1];
    var _f = useSearchController({
        options: options,
        wait: wait,
        queryOptions: __assign(__assign({}, queryOptions), { onSuccess: function (data) {
                setOpen(true);
                (queryOptions === null || queryOptions === void 0 ? void 0 : queryOptions.onSuccess) && queryOptions.onSuccess(data);
            }, onError: function (error) {
                setOpen(true);
                (queryOptions === null || queryOptions === void 0 ? void 0 : queryOptions.onError) && queryOptions.onError(error);
            } }),
        disableHighlight: disableHighlight,
    }), searchData = _f.searchData, query = _f.query, doSearch = _f.doSearch;
    var contextValue = useMemo(function () { return (__assign(__assign({}, searchData), { onClose: function () { return onNavigate(); } })); }, [searchData, onNavigate]);
    var handleChange = useCallback(function (event) {
        setOpen(false);
        var query = event.target
            ? event.target.value
            : event;
        doSearch(query);
    }, [doSearch]);
    return (_jsxs(_Fragment, { children: [_jsx(SearchInput, __assign({ onChange: handleChange, value: query, 
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus: true, fullWidth: true }, rest)), query && open ? (searchData.error ? (_jsx(Typography, { color: "error", variant: "body2", children: translate('ra.notification.http_error', {
                    _: 'Server communication error',
                }) })) : (_jsx(SearchResultContextProvider, { value: contextValue, children: children }))) : null] }));
};
